import React, { useCallback, useMemo } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { PlanStep, PlanPhase, StepStatus, Participant, PlanRole, ChatMessage, StepAutoAction } from '../../../types/generated/graphql';
import { useMutation, useQuery } from '@apollo/client';
import { SEND_CHAT_MESSAGE, GET_PLAN, GET_CONVERSATION } from '../../../graphql/queries';
import StepListPane from './StepListPane';
import StepWorkspacePane from './StepWorkspacePane';
import { useToast } from '../../../contexts/ToastContext';

interface TasksTwoPaneLayoutProps {
  planId: string;
  steps: PlanStep[];
  phases: PlanPhase[];
  participants: Participant[];
  roles?: PlanRole[];
  selectedStepId: string | null;
  onStepSelect: (stepId: string | null) => void;
  onToggleStepCompletion: (stepId: string, completed: boolean) => void;
}

export const TasksTwoPaneLayout: React.FC<TasksTwoPaneLayoutProps> = ({
  planId,
  steps,
  phases,
  participants,
  roles = [],
  selectedStepId: propSelectedStepId,
  onStepSelect,
  onToggleStepCompletion,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showToast } = useToast();

  // Use prop value or default to first incomplete step
  const selectedStepId = useMemo(() => {
    if (propSelectedStepId) {
      return propSelectedStepId;
    }
    // Default to first incomplete step, or first step
    const firstIncomplete = steps.find((s) => s.status !== StepStatus.Completed);
    return firstIncomplete?.id || steps[0]?.id || null;
  }, [propSelectedStepId, steps]);

  // Get selected step object
  const selectedStep = useMemo(
    () => steps.find((s) => s.id === selectedStepId) || null,
    [steps, selectedStepId]
  );

  // Fetch conversation for the plan
  const { data: conversationData } = useQuery(GET_CONVERSATION, {
    variables: { planId },
    skip: !planId,
  });

  // Get messages from conversation
  const messages: ChatMessage[] = useMemo(() => {
    return conversationData?.getConversation?.messages || [];
  }, [conversationData]);

  // Send message mutation (for copilot interactions)
  const [sendMessage, { loading: isSending }] = useMutation(SEND_CHAT_MESSAGE, {
    refetchQueries: [
      { query: GET_PLAN, variables: { id: planId } },
      { query: GET_CONVERSATION, variables: { planId } },
    ],
  });

  // Handle step selection - update URL via parent
  const handleSelectStep = useCallback((stepId: string) => {
    onStepSelect(stepId);
  }, [onStepSelect]);

  // Handle sending a message
  const handleSendMessage = useCallback(
    async (text: string) => {
      if (!selectedStepId) return;

      // Prepend step context to the message for display/filtering in feed
      const contextPrefix = `[Step: ${selectedStep?.title}]\n\n`;
      const messageWithContext = contextPrefix + text;

      try {
        const result = await sendMessage({
          variables: {
            input: {
              planId,
              message: messageWithContext,
              stepId: selectedStepId, // Triggers step assessment on backend
            },
          },
        });

        // Handle auto-action (step auto-completed)
        const autoAction = result.data?.sendChatMessage?.autoAction as StepAutoAction | null;
        if (autoAction) {
          console.log('Auto-action received:', autoAction);

          // Show notification
          showToast({
            message: autoAction.message,
            severity: 'success',
            autoHideDuration: 4000,
          });

          // Auto-advance to next step if available
          if (autoAction.nextStepId) {
            // Small delay to let the user see the notification
            setTimeout(() => {
              onStepSelect(autoAction.nextStepId!);
            }, 500);
          }
        }
      } catch (err) {
        console.error('Failed to send message:', err);
      }
    },
    [planId, selectedStepId, selectedStep?.title, sendMessage, onStepSelect, showToast]
  );

  // Handle step completion toggle - fire and forget
  const handleToggleCompletion = useCallback(
    (stepId: string, completed: boolean) => {
      onToggleStepCompletion(stepId, completed);
    },
    [onToggleStepCompletion]
  );

  // Mobile layout: stacked vertically
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Step list on top - auto height */}
        <StepListPane
          steps={steps}
          phases={phases}
          participants={participants}
          roles={roles}
          selectedStepId={selectedStepId}
          onSelectStep={handleSelectStep}
        />

        {/* Workspace below - auto height with sticky copilot */}
        <StepWorkspacePane
          planId={planId}
          step={selectedStep}
          participants={participants}
          roles={roles}
          messages={messages}
          onSendMessage={handleSendMessage}
          onToggleCompletion={handleToggleCompletion}
          isChatLoading={isSending}
          allSteps={steps}
          onSelectStep={handleSelectStep}
        />

      </Box>
    );
  }

  // Desktop layout: side by side
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
      }}
    >
      {/* Left pane: Step list - auto height */}
      <Box sx={{ width: 320, flexShrink: 0 }}>
        <StepListPane
          steps={steps}
          phases={phases}
          participants={participants}
          roles={roles}
          selectedStepId={selectedStepId}
          onSelectStep={handleSelectStep}
        />
      </Box>

      {/* Right pane: Step workspace - auto height with sticky copilot */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <StepWorkspacePane
          planId={planId}
          step={selectedStep}
          participants={participants}
          roles={roles}
          messages={messages}
          onSendMessage={handleSendMessage}
          onToggleCompletion={handleToggleCompletion}
          isChatLoading={isSending}
          allSteps={steps}
          onSelectStep={handleSelectStep}
        />
      </Box>

    </Box>
  );
};

export default TasksTwoPaneLayout;
